import { useState, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Loader2 } from "lucide-react";
import { hardenedSubmit } from "@/lib/submit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full h-11 px-3 rounded-md border border-amber-500/30 bg-[#0f1629] text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/40 transition-colors";
const selectCls = "w-full h-11 px-3 rounded-md border border-amber-500/30 bg-[#0f1629] text-white text-sm focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/40 appearance-none transition-colors";
const labelCls = "block text-[11px] font-bold text-amber-400/90 uppercase tracking-wider mb-1.5";

function SignaturePad({ onSignatureChange, onClear }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const signed = useRef(false);

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (canvas.width !== rect.width || canvas.height !== rect.height) {
      canvas.width = rect.width;
      canvas.height = rect.height;
    }
  }, []);

  useEffect(() => {
    resize();
    const ro = new ResizeObserver(resize);
    if (canvasRef.current) ro.observe(canvasRef.current);
    return () => ro.disconnect();
  }, [resize]);

  const getXY = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: cx - rect.left, y: cy - rect.top };
  };

  const onDown = (e) => {
    e.preventDefault();
    resize();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getXY(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    drawing.current = true;
  };

  const onMove = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getXY(e);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#d4af37";
    ctx.lineTo(x, y);
    ctx.stroke();
    signed.current = true;
  };

  const onUp = () => {
    if (drawing.current) {
      drawing.current = false;
      if (signed.current) onSignatureChange(canvasRef.current.toDataURL());
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    signed.current = false;
    onSignatureChange("");
    if (onClear) onClear();
  };

  return (
    <div>
      <label className={labelCls}>Sign Below *</label>
      <canvas
        ref={canvasRef}
        data-testid="signature-canvas"
        className="w-full rounded-md border border-amber-500/30 bg-[#0f1629] cursor-crosshair touch-none"
        style={{ height: "140px" }}
        onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
        onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}
      />
      <button type="button" onClick={clear} data-testid="clear-signature-btn"
        className="mt-2 px-4 py-1.5 rounded-md border border-amber-500/30 bg-[#0f1629] text-xs font-bold text-amber-400/80 uppercase tracking-wider hover:border-amber-400 transition-colors">
        Clear Signature
      </button>
    </div>
  );
}

function getTodayFormatted() {
  return new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
}

function ACHConfirmationScreen({ achConfNum, navigate }) {
  const [cd, setCd] = useState(6);
  useEffect(() => {
    const timer = setInterval(() => {
      setCd((p) => {
        if (p <= 1) { clearInterval(timer); navigate("/"); return 0; }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 bg-[#0a0e1a]" data-testid="ach-success">
      <div className="max-w-md w-full rounded-xl border border-slate-700/50 bg-[#111827]/70 p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-5">
          <CheckCircle className="w-8 h-8 text-emerald-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">ACH Authorization Complete!</h2>
        <p className="text-sm text-slate-400 leading-relaxed mb-6">Your ACH authorization has been received. A member of our team will be in contact shortly.</p>
        <div className="rounded-lg border border-emerald-500/30 bg-[#0f1629] p-4 mb-6">
          <p className="text-xs text-slate-500 uppercase tracking-widest mb-1">ACH Confirmation Number</p>
          <p className="text-xl font-mono font-bold text-emerald-400" data-testid="ach-conf-display">{achConfNum}</p>
        </div>
        <p className="text-sm text-slate-400 mb-4">Returning to home in <span className="text-amber-400 font-bold">{cd}</span> seconds...</p>
        <div className="w-full h-1.5 rounded-full bg-slate-800 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-1000 ease-linear" style={{ width: `${((6 - cd) / 6) * 100}%` }} />
        </div>
      </div>
    </div>
  );
}

export default function ACHPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [achConfNum, setAchConfNum] = useState("");
  const [countdown, setCountdown] = useState(6);
  const [consent, setConsent] = useState(false);
  const [signatureData, setSignatureData] = useState("");
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "",
    bank_name: "", routing_number: "", account_number: "",
    account_type: "", approved_ach_amount: "",
    company_website: "",
  });

  const set = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!consent) { toast.error("Please accept the authorization consent."); return; }
    if (!signatureData) { toast.error("Please provide your signature."); return; }
    setLoading(true);
    try {
      const result = await hardenedSubmit("/ach", {
        business_name: form.full_name,
        account_holder_name: form.full_name,
        bank_name: form.bank_name,
        routing_number: form.routing_number,
        account_number: form.account_number,
        account_type: form.account_type,
        authorization_consent: consent,
        signature_data: signatureData,
        applicant_email: form.email,
        phone: form.phone,
        company_website: form.company_website,
      });
      setAchConfNum(result.confirmation_number);
      setSuccess(true);
      toast.success("ACH authorization submitted successfully!");
    } catch (err) {
      toast.error(err.message || "Submission failed. Please try again.");
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  if (success) {
    return <ACHConfirmationScreen achConfNum={achConfNum} navigate={navigate} />;
  }

  return (
    <div className="py-10 md:py-14 px-4 bg-ach min-h-screen" data-testid="ach-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-2" data-testid="ach-heading">ACH Payment Authorization</h1>
          <p className="text-sm text-slate-400">Securely authorize automatic payments from your bank account.</p>
        </div>

        {/* Security notice */}
        <div className="mb-8 px-5 py-3 rounded-md border border-amber-500/30 bg-amber-500/5">
          <p className="text-sm text-amber-300 font-medium">Your banking information is encrypted and securely transmitted. We will never share your details with third parties.</p>
        </div>

        {/* Form Card */}
        <div className="rounded-xl border border-slate-700/50 bg-[#111827]/70 backdrop-blur-sm p-6 sm:p-8">
          <form onSubmit={handleSubmit} data-testid="ach-form">
            <div className="hp-field" aria-hidden="true">
              <input type="text" name="company_website" tabIndex={-1} autoComplete="off" value={form.company_website} onChange={set} />
            </div>

            {/* Personal Information */}
            <h2 className="text-lg font-bold text-amber-400 italic mb-5" data-testid="section-personal">Personal Information</h2>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Full Name *</label>
                <input data-testid="ach-full-name" className={inputCls} name="full_name" required value={form.full_name} onChange={set} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Email Address *</label>
                  <input data-testid="ach-email" className={inputCls} name="email" type="email" required value={form.email} onChange={set} />
                </div>
                <div>
                  <label className={labelCls}>Phone Number *</label>
                  <input data-testid="ach-phone" className={inputCls} name="phone" type="tel" required value={form.phone} onChange={set} />
                </div>
              </div>
            </div>

            {/* Bank Information */}
            <h2 className="text-lg font-bold text-amber-400 italic mt-8 mb-5" data-testid="section-bank">Bank Information</h2>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Bank Name *</label>
                <input data-testid="ach-bank-name" className={inputCls} name="bank_name" required value={form.bank_name} onChange={set} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Routing Number *</label>
                  <input data-testid="ach-routing-number" className={inputCls} name="routing_number" required value={form.routing_number} onChange={set} />
                </div>
                <div>
                  <label className={labelCls}>Account Number *</label>
                  <input data-testid="ach-account-number" className={inputCls} name="account_number" required value={form.account_number} onChange={set} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Account Type *</label>
                  <select data-testid="ach-account-type" className={selectCls} name="account_type" required value={form.account_type} onChange={set}>
                    <option value="">Select...</option>
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Approved ACH Amount *</label>
                  <input data-testid="ach-amount" className={inputCls} name="approved_ach_amount" required value={form.approved_ach_amount} onChange={set} />
                </div>
              </div>
            </div>

            {/* Authorization Signature */}
            <h2 className="text-lg font-bold text-amber-400 italic mt-8 mb-5" data-testid="section-signature">Authorization Signature</h2>
            <SignaturePad onSignatureChange={setSignatureData} />

            {/* Consent checkbox */}
            <div className="flex items-start gap-3 mt-6">
              <input
                type="checkbox"
                id="ach-consent"
                data-testid="ach-consent-checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-amber-500/40 accent-amber-500"
              />
              <label htmlFor="ach-consent" className="text-sm text-slate-400 leading-relaxed cursor-pointer">
                I confirm the information provided is true and correct. I authorize EasyQuickFund to debit my account via ACH for the agreed-upon services.
              </label>
            </div>

            {/* Date + Note */}
            <div className="mt-6 px-4 py-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
              <p className="text-sm text-slate-300"><span className="font-bold text-amber-400">Date:</span> {getTodayFormatted()}</p>
              <p className="text-sm text-slate-300 mt-1"><span className="font-bold text-amber-400">Note:</span> Your ACH will not be processed until funds are deposited into your bank account.</p>
            </div>

            {/* Security banner */}
            <div data-testid="security-banner" className="mt-6 flex items-center gap-3 px-4 py-3 rounded-lg border border-amber-500/20 bg-[#151d2e]">
              <svg className="w-4 h-4 text-amber-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              <p className="text-xs text-slate-400 leading-relaxed">Your information is encrypted and securely transmitted. We never share your data with third parties.</p>
            </div>

            {/* Submit */}
            <button type="submit" disabled={loading} data-testid="ach-submit-btn"
              className="w-full mt-6 h-12 rounded-md bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-[#0a0e1a] font-bold text-sm uppercase tracking-wider transition-all disabled:opacity-50">
              {loading ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</span> : "SUBMIT ACH AUTHORIZATION"}
            </button>

            <p className="text-center text-xs text-slate-500 mt-4">
              By submitting, you agree to our{" "}
              <Link to="/privacy" className="text-amber-400 hover:underline">Privacy Policy</Link>
              {" "}and{" "}
              <Link to="/terms" className="text-amber-400 hover:underline">Terms of Service</Link>.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
